import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { forgotPassword } from '@/features/auth/services/authApi';

const RESEND_COOLDOWN = 5 * 60; // 5 minutes in seconds

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [submitted, setSubmitted] = useState(false);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const emailRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    emailRef.current?.focus();
  }, []);

  const startCooldown = useCallback((seconds: number) => {
    if (timerRef.current) clearInterval(timerRef.current);
    setCooldown(seconds);
    timerRef.current = setInterval(() => {
      setCooldown((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, []);

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (cooldown > 0 || loading) return;
    setError('');
    setMessage('');
    setLoading(true);
    try {
      const result = await forgotPassword(email);
      setMessage(result.message);
      setSubmitted(true);
      startCooldown(RESEND_COOLDOWN);
    } catch (err: unknown) {
      const resp = (err as { response?: { status?: number; data?: { error?: string; retry_after?: number } } })?.response;
      if (resp?.status === 429 && resp.data) {
        if (resp.data.retry_after) {
          startCooldown(resp.data.retry_after);
        }
        setError(resp.data.error || 'Please wait before requesting another reset link.');
      } else {
        setError('Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-sm">
        <h1 className="text-xl font-bold mb-2 text-accent">Forgot Password</h1>
        <p className="text-sm text-muted mb-6">
          Enter your email and we'll send you a link to reset your password.
        </p>
        <hr className="mb-6" />

        {error && <p className="text-sm text-red-500 mb-4">{error}</p>}
        {message && <p className="text-sm text-muted mb-4">{message}</p>}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="email" className="block text-xs font-bold mb-1">Email</label>
            <input
              id="email"
              ref={emailRef}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="w-full px-2 py-1.5 border border-border text-sm font-mono focus:outline-none focus:border-accent"
            />
          </div>
          <button
            type="submit"
            disabled={loading || cooldown > 0}
            className="btn-terminal disabled:opacity-50"
          >
            {loading
              ? 'Sending...'
              : cooldown > 0
                ? `Resend link (${formatTime(cooldown)})`
                : submitted
                  ? 'Resend link'
                  : 'Send Reset Link'}
          </button>
        </form>

        <p className="text-sm text-muted mt-6">
          <Link to="/login">Back to Sign In</Link>
        </p>
      </div>
    </div>
  );
}
